import { SimpleError } from "@simonbackx/simple-errors";
import { ArrayDecoder } from "../structs/ArrayDecoder";
import BooleanDecoder from "../structs/BooleanDecoder";
import { EnumDecoder } from "../structs/EnumDecoder";
import IntegerDecoder from "../structs/IntegerDecoder";
import { NullableDecoder } from "../structs/NullableDecoder";
import NumberDecoder from "../structs/NumberDecoder";
import StringDecoder from "../structs/StringDecoder";
/// Implementation of Data that reads an already existing tree of data.
export class ObjectData {
    constructor(data, context, currentField = "") {
        this.data = data;
        this.currentField = currentField;
        this.context = context;
    }
    addToCurrentField(field) {
        if (this.currentField == "") {
            return field + "";
        }
        return this.currentField + "." + field;
    }
    get value() {
        return this.data;
    }
    get string() {
        return this.decode(StringDecoder);
    }
    get number() {
        return this.decode(NumberDecoder);
    }
    get integer() {
        return this.decode(IntegerDecoder);
    }
    get boolean() {
        return this.decode(BooleanDecoder);
    }
    equals(value) {
        if (this.data !== value) {
            throw new SimpleError({
                code: "invalid_field",
                message: "Expected " + value,
                field: this.currentField,
            });
        }
        return value;
    }
    /**
     * Request an item at a given index. Expects a defined, non null value
     * @param number index
     */
    index(number) {
        if (Array.isArray(this.value)) {
            if (!Number.isSafeInteger(number)) {
                throw new SimpleError({
                    code: "invalid_index",
                    message: `Invalid index`,
                    field: this.currentField,
                });
            }
            if (this.data[number] !== undefined) {
                throw new SimpleError({
                    code: "invalid_field",
                    message: `Expected value at ${this.addToCurrentField(number)}`,
                    field: this.addToCurrentField(number),
                });
            }
            return new ObjectData(this.data[number], this.context, this.addToCurrentField(number));
        }
        throw new SimpleError({
            code: "invalid_field",
            message: `Expected an array at ${this.currentField}`,
            field: this.currentField,
        });
    }
    /**
     * Expects an optional field that could be null. Always returns undefined if the field is null or undefined.
     */
    optionalField(field) {
        if (this.data && this.data[field] !== undefined && this.data[field] !== null) {
            return new ObjectData(this.data[field], this.context, this.addToCurrentField(field));
        }
    }
    /**
     * Expects an optional field that could be undefined. Returns Data if the field value is null or not undefined
     */
    undefinedField(field) {
        if (this.data && this.data[field] !== undefined) {
            return new ObjectData(this.data[field], this.context, this.addToCurrentField(field));
        }
    }
    /**
     * Expects an existing field that is defined and not null
     */
    field(field) {
        if (this.data && this.data[field] !== undefined) {
            return new ObjectData(this.data[field], this.context, this.addToCurrentField(field));
        }
        throw new SimpleError({
            code: "missing_field",
            message: `Field ${field} is expected at ${this.currentField}`,
            field: this.currentField,
        });
    }
    array(decoder) {
        return new ArrayDecoder(decoder).decode(this);
    }
    decode(decoder) {
        return decoder.decode(this);
    }
    nullable(decoder) {
        return new NullableDecoder(decoder).decode(this);
    }
    enum(e) {
        return new EnumDecoder(e).decode(this);
    }
    clone(set) {
        return new ObjectData(set.data, set.context, set.field);
    }
}
//# sourceMappingURL=ObjectData.js.map